import React from 'react';
import { motion } from 'framer-motion';
import { Target, Eye, Flag } from 'lucide-react';
import { Reveal, SectionHeading } from '../SectionHeading';
import { TIMELINE } from '../../data/siteData';

const pillars = [
  { icon: Target, title: 'Mission', desc: 'To make reliable, trained and accountable workforce accessible to every growing business in India — at speed and at scale.' },
  { icon: Eye, title: 'Vision', desc: 'To become India\u2019s most trusted workforce infrastructure company, powering operations for the enterprises and AI-era companies of tomorrow.' },
  { icon: Flag, title: 'Long-Term Goal', desc: 'Build a million-strong skilled workforce network spanning every major Indian city, delivering execution excellence across industries.' },
];

export const AboutSection = () => (
  <section id="about" data-testid="about-section" className="py-20 md:py-32 bg-[#F8FAFC]">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <SectionHeading align="left" label="About WorkLynx" title="Who We Are" />
          <Reveal className="-mt-8 space-y-5 text-slate-600 leading-relaxed text-base">
            <p>
              WorkLynx was founded on a simple observation: India&apos;s fastest-growing companies were being slowed
              down not by a lack of ambition, but by the friction of finding, training and managing reliable workforce.
            </p>
            <p>
              Starting from Bokaro and expanding to Delhi NCR and 15+ cities, we built a workforce
              infrastructure company — one that recruits, trains, deploys and manages teams for workforce
              operations, AI data programs, robotics operations, hiring and BPO delivery.
            </p>
            <p>
              Today, with <strong className="text-[#0A192F]">100,000+ hours executed</strong> and{' '}
              <strong className="text-[#0A192F]">500+ workforce managed</strong>, we partner with e-commerce
              leaders, AI startups, logistics networks and enterprises to help them execute efficiently across India.
            </p>
          </Reveal>
          <div className="mt-10 grid gap-4">
            {pillars.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.1} className="flex gap-4 p-5 bg-white border border-slate-200 rounded-lg hover:shadow-md hover:-translate-y-0.5 transition-all duration-300">
                <div className="w-11 h-11 rounded-md bg-[#0A192F] flex items-center justify-center shrink-0">
                  <p.icon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#0A192F] font-heading">{p.title}</h3>
                  <p className="text-sm text-slate-600 mt-1 leading-relaxed">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7">
          <Reveal className="mb-10">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#0066CC] mb-4">Our Journey</div>
            <h3 className="text-2xl sm:text-3xl font-semibold text-[#0A192F] font-heading">Growth Timeline</h3>
          </Reveal>
          <div className="relative pl-8">
            <motion.div
              initial={{ scaleY: 0 }}
              whileInView={{ scaleY: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.2, ease: 'easeOut' }}
              className="absolute left-[7px] top-2 bottom-2 w-0.5 bg-gradient-to-b from-[#0066CC] to-[#0A192F] origin-top"
            />
            <div className="space-y-10">
              {TIMELINE.map((t, i) => (
                <Reveal key={t.title} delay={i * 0.15} className="relative" data-testid={`timeline-item-${i}`}>
                  <span className="absolute -left-8 top-1.5 w-4 h-4 rounded-full bg-white border-[3px] border-[#0066CC]" />
                  <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#0066CC]">{t.year}</div>
                  <h4 className="text-lg font-semibold text-[#0A192F] font-heading mt-1">{t.title}</h4>
                  <p className="text-sm text-slate-600 mt-1.5 leading-relaxed max-w-lg">{t.desc}</p>
                </Reveal>
              ))}
            </div>
          </div>
          <Reveal delay={0.3} className="mt-12 rounded-xl overflow-hidden border border-slate-200 shadow-lg">
            <img src="https://images.pexels.com/photos/12903168/pexels-photo-12903168.jpeg?auto=compress&cs=tinysrgb&w=1200" alt="WorkLynx team collaborating" loading="lazy" className="w-full h-64 object-cover" />
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);
